import type { FC } from 'react';
import { useMemo } from 'react';
import { Empty, Spin, Timeline, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TicketWorkOrderEventDto } from '@evimo/client/models';
import { useTicketDetailTimelineItems } from './hooks/useTicketDetailTimelineItems';
import { isTicketTimelineEvent } from './utils';

export interface TicketDetailTimelineProps {
  events: TicketWorkOrderEventDto[];
  actorNameById: Map<string, string>;
  loading?: boolean;
  compact?: boolean;
  showHint?: boolean;
}

export const TicketDetailTimeline: FC<TicketDetailTimelineProps> = ({
  events,
  actorNameById,
  loading = false,
  compact = false,
  showHint = true,
}) => {
  const { t } = useTranslation('maintenance');
  const timelineEvents = useMemo(
    () => events.filter((event) => isTicketTimelineEvent(event.eventType)),
    [events],
  );
  const items = useTicketDetailTimelineItems({
    events: timelineEvents,
    actorNameById,
    compact,
  });

  return (
    <Spin spinning={loading}>
      {timelineEvents.length === 0 && !loading ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('workOrders.ticketDetail.timeline.empty')}
        />
      ) : (
        <>
          {showHint ? (
            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginBottom: 12, fontSize: 12 }}
            >
              {t('workOrders.ticketDetail.timeline.hint')}
            </Typography.Text>
          ) : null}
          <Timeline items={items} />
        </>
      )}
    </Spin>
  );
};
